import React, { useState, useEffect, useRef, useContext } from "react";
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaChevronLeft,
  FaChevronRight,
  FaClosedCaptioning,
} from "react-icons/fa";
import { LanguageContext } from "../../contexts/LanguageContext/LanguageContext";

const DEFAULT_BG = "#d1d5db";

// Helper to detect mobile
const isMobile = () =>
  typeof window !== "undefined" &&
  (window.innerWidth < 768 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ));

const TranslationDisplay = ({ configPath, onBackgroundColorChange }) => {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState({
    type: "none", // "original", "translated", or "none"
    idx: 0,
  });
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [captionsOnOriginal, setCaptionsOnOriginal] = useState(false);
  const [captionsOnTranslated, setCaptionsOnTranslated] = useState(false);
  const originalVideoRef = useRef(null);
  const translatedVideoRef = useRef(null);

  const { languages, loading: languagesLoading } = useContext(LanguageContext);

  useEffect(() => {
    fetch(configPath, { cache: "no-cache" })
      .then((res) => res.json())
      .then(setConfig)
      .catch((err) => setError(err.message));
  }, [configPath]);

  useEffect(() => {
    if (!config) return;
    const { videos } = config;
    const resources = [];
    videos.forEach((video) => {
      resources.push(video.originalPath);
      if (Array.isArray(video.translated)) {
        video.translated.forEach((t) => resources.push(t.translatedPath));
      }
    });
    Promise.all(
      resources.map(
        (src) =>
          new Promise((resolve) => {
            const v = document.createElement("video");
            v.src = src;
            v.preload = "auto";
            v.onloadeddata = resolve;
            v.onerror = resolve;
          })
      )
    ).then(() => setIsPreloaded(true));
  }, [config]);

  const getFlagUrl = (langName) => {
    if (!languages || !langName) return "";
    const lang = languages.find(
      (l) =>
        l.name.toLowerCase() === langName.toLowerCase() ||
        (l.native_name &&
          l.native_name.toLowerCase() === langName.toLowerCase())
    );
    if (lang && lang.flag) {
      const flagUrl = `${lang.flag}`;
      return flagUrl;
    }
    return "";
  };

  const handlePlay = (type, idx) => {
    setSelectedVideo({ type, idx });
    setTimeout(() => {
      let videoRef =
        type === "original" ? originalVideoRef : translatedVideoRef;
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.muted = isMuted;
        videoRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const handlePause = () => {
    let videoRef =
      selectedVideo.type === "original"
        ? originalVideoRef
        : selectedVideo.type === "translated"
        ? translatedVideoRef
        : null;
    if (videoRef && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTogglePlay = () => {
    let videoRef =
      selectedVideo.type === "original"
        ? originalVideoRef
        : selectedVideo.type === "translated"
        ? translatedVideoRef
        : null;
    if (videoRef && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleToggleMute = () => {
    setIsMuted((prev) => {
      let newMuted = !prev;
      if (originalVideoRef.current) originalVideoRef.current.muted = newMuted;
      if (translatedVideoRef.current)
        translatedVideoRef.current.muted = newMuted;
      return newMuted;
    });
  };

  useEffect(() => {
    setSelectedVideo({ type: "none", idx: 0 });
    setIsPlaying(false);
  }, [carouselIndex]);

  // Pause video if user switches between original/translated
  useEffect(() => {
    if (originalVideoRef.current) originalVideoRef.current.pause();
    if (translatedVideoRef.current) translatedVideoRef.current.pause();
    setIsPlaying(false);
  }, [selectedVideo.type, selectedVideo.idx]);

  // Ensure only the active video's captions are visible (fixes stacking bug)
  useEffect(() => {
    // Hide all captions in all videos on the page
    const allVideos = document.querySelectorAll("video");
    allVideos.forEach((vid) => {
      if (vid.textTracks) {
        for (let i = 0; i < vid.textTracks.length; i++) {
          vid.textTracks[i].mode = "hidden";
        }
      }
    });

    // Show captions only for the active video
    if (selectedVideo.type === "original" && originalVideoRef.current) {
      const tracks = originalVideoRef.current.textTracks;
      if (tracks && tracks.length > 0) {
        tracks[0].mode = captionsOnOriginal ? "showing" : "hidden";
      }
    }
    if (selectedVideo.type === "translated" && translatedVideoRef.current) {
      const tracks = translatedVideoRef.current.textTracks;
      if (tracks && tracks.length > 0) {
        tracks[0].mode = captionsOnTranslated ? "showing" : "hidden";
      }
    }
  }, [captionsOnOriginal, captionsOnTranslated, selectedVideo, carouselIndex]);

  if (error) {
    return (
      <div className="text-red-500 p-5 bg-red-50 rounded-lg text-center text-lg">
        Error: {error}
      </div>
    );
  }
  const mobile = isMobile();

  if (!config || (!mobile && !isPreloaded) || languagesLoading) {
    return (
      <div className="text-gray-600 p-5 bg-gray-100 rounded-lg text-center text-lg">
        Loading...
      </div>
    );
  }
  const { videos } = config;
  const video = videos[carouselIndex];
  const translations = Array.isArray(video.translated) ? video.translated : [];
  const translation = translations[selectedVideo.idx];

  const showTranslatedLabel =
    selectedVideo.type === "translated" &&
    translation &&
    translation.language &&
    translation.language.toLowerCase() === "english";

  const showOriginalVideo = selectedVideo.type === "original";
  const showTranslatedVideo = selectedVideo.type === "translated";
  const showCarouselControls = Array.isArray(videos) && videos.length > 1;

  return (
    <div className="reel-container w-full max-w-7xl mx-auto p-6 bg-gradient-to-br from-gray-50 to-gray-200 rounded-2xl shadow-xl overflow-x-hidden">
      {/* Carousel navigation */}
      {showCarouselControls && (
        <div className="flex items-center justify-center gap-6 mb-6">
          <button
            className="p-3 rounded-full bg-red-500 text-white shadow-lg border-2 hover:bg-red-600 hover:scale-110 transition-all duration-200"
            onClick={() =>
              setCarouselIndex(
                (prev) => (prev - 1 + videos.length) % videos.length
              )
            }
            aria-label="Previous video"
          >
            <FaChevronLeft className="text-2xl text-blue-900" />
          </button>
          <span className="font-semibold text-lg md:text-xl text-gray-900">
            {" "}
            {videos[carouselIndex].channelName}
          </span>
          <button
            className="p-3 rounded-full bg-red-500 text-white shadow-lg border-2 hover:bg-red-600 hover:scale-110 transition-all duration-200"
            onClick={() =>
              setCarouselIndex((prev) => (prev + 1) % videos.length)
            }
            aria-label="Next video"
          >
            <FaChevronRight className="text-2xl text-blue-900" />
          </button>
        </div>
      )}

      <div className="split-screen flex flex-col md:flex-row w-full max-w-[1920px] mx-auto gap-4 md:gap-6">
        {/* Original video section */}
        <div className="video-section flex-1 flex flex-col overflow-hidden bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
          <div className="video-wrapper relative w-full overflow-hidden bg-gray-900 rounded-t-xl md:aspect-video md:max-h-[40vh] flex items-center justify-center">
            {showOriginalVideo ? (
              <div className="relative w-full h-full flex items-center justify-center">
                <video
                  ref={originalVideoRef}
                  src={video.originalPath}
                  muted={isMuted}
                  playsInline
                  preload="auto"
                  className="w-full h-auto object-cover block md:h-full"
                  aria-label="Original video preview"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  {video.captionPath && video.captionPath.endsWith(".vtt") && (
                    <track
                      label="Captions"
                      kind="subtitles"
                      srcLang="en"
                      src={video.captionPath}
                      default
                    />
                  )}
                </video>
                {/* Custom controls */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4 bg-white/80 rounded-lg px-4 py-2 shadow">
                  <button
                    onClick={handleTogglePlay}
                    className="text-xl text-gray-900"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <button
                    onClick={handleToggleMute}
                    className="text-xl text-gray-900"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                </div>
              </div>
            ) : (
              <img
                src={video.mobileImageOriginal}
                alt="Original preview"
                className="w-full h-full object-cover block md:h-full rounded-t-xl"
                style={{ maxHeight: "320px" }}
              />
            )}
          </div>
          {/* Captions toggle below video */}
          <div className="flex items-center gap-2 mt-2 mb-2 px-4">
            <FaClosedCaptioning className="text-2xl text-blue-900" />
            <label
              htmlFor="captions-toggle"
              className="font-medium text-blue-900"
            >
              Closed Captions
            </label>
            <input
              id="captions-toggle"
              type="checkbox"
              checked={captionsOnOriginal}
              onChange={() => setCaptionsOnOriginal((v) => !v)}
            />
          </div>
          <div className="label text-center text-lg font-semibold py-4 bg-gray-100 rounded-b-xl text-gray-900">
            Original: {video.channelName || "Unknown"}
          </div>
        </div>
        {/* Translated video section */}
        <div className="video-section flex-1 flex flex-col overflow-hidden bg-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
          <div className="video-wrapper relative w-full overflow-hidden bg-gray-900 rounded-t-xl md:aspect-video md:max-h-[40vh] flex items-center justify-center">
            {showTranslatedVideo && translation ? (
              <div className="relative w-full h-full flex items-center justify-center">
                <video
                  ref={translatedVideoRef}
                  src={translation.translatedPath}
                  muted={isMuted}
                  playsInline
                  preload="auto"
                  className="w-full h-auto object-cover block md:h-full"
                  aria-label="Translated video preview"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  {translation.captionPath &&
                    translation.captionPath.endsWith(".vtt") && (
                      <track
                        label="Captions"
                        kind="subtitles"
                        srcLang={translation.language?.toLowerCase() || "en"}
                        src={translation.captionPath}
                        default
                      />
                    )}
                </video>
                {/* Custom controls */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4 bg-white/80 rounded-lg px-4 py-2 shadow">
                  <button
                    onClick={handleTogglePlay}
                    className="text-xl text-gray-900"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <button
                    onClick={handleToggleMute}
                    className="text-xl text-gray-900"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                </div>
              </div>
            ) : (
              <img
                src={video.mobileImageOriginal}
                alt="Translation preview"
                className="w-full h-full object-cover block md:h-full rounded-t-xl"
                style={{ maxHeight: "320px" }}
              />
            )}
          </div>
          {/* Captions toggle below video */}
          <div className="flex items-center gap-2 mt-2 mb-2 px-4">
            <FaClosedCaptioning className="text-2xl text-blue-900" />
            <label
              htmlFor="captions-toggle-translation"
              className="font-medium text-blue-900"
            >
              Closed Captions
            </label>
            <input
              id="captions-toggle-translation"
              type="checkbox"
              checked={captionsOnTranslated}
              onChange={() => setCaptionsOnTranslated((v) => !v)}
            />
          </div>
          <div className="label text-center text-lg font-semibold py-4 bg-gray-100 rounded-b-xl text-gray-900">
            {showTranslatedLabel
              ? `Translated: ${translation.language}`
              : "Translated:"}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="translation-controls mt-8 p-6 bg-white rounded-xl shadow-sm">
        <div className="flex flex-col gap-6 items-start">
          {/* Play Original & Play Translations */}
          <div className="flex flex-row items-center gap-3 w-full">
            <span className="text-base font-medium text-blue-900 min-w-[8rem]">
              Play Original
            </span>
            <button
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white border border-gray-300 text-black font-semibold shadow-sm transition text-sm ${
                selectedVideo.type === "original" ? "ring-2 ring-blue-300" : ""
              }`}
              onClick={() => handlePlay("original", 0)}
            >
              <img
                src={getFlagUrl(video.translatedFrom)}
                alt={`${video.translatedFrom} flag`}
                className="w-10 h-5 rounded-md object-cover"
              />
              <span className="flex items-center gap-1">
                <FaPlay className="inline" />
                <span>{video.translatedFrom}</span>
              </span>
            </button>
          </div>
          <div className="flex flex-row items-center gap-3 w-full">
            <span className="text-base font-medium text-blue-900 min-w-[8rem]">
              Play Translations
            </span>
            <div className="flex flex-wrap gap-2 justify-start">
              {translations.map((t, idx) => (
                <button
                  key={t.language + idx}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white border border-gray-300 text-black font-semibold shadow-sm transition text-sm ${
                    selectedVideo.type === "translated" &&
                    selectedVideo.idx === idx
                      ? "ring-2 ring-blue-300"
                      : ""
                  }`}
                  onClick={() => handlePlay("translated", idx)}
                  disabled={
                    selectedVideo.type === "translated" &&
                    selectedVideo.idx === idx
                  }
                >
                  <img
                    src={getFlagUrl(t.language)}
                    alt={`${t.language} flag`}
                    className="w-10 h-5 rounded-md object-cover"
                  />
                  <span className="flex items-center gap-1">
                    <FaPlay className="inline" />
                    <span>{t.language}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationDisplay;
